import { useEffect, useState, useRef } from "react";
import {
  Box,
  Heading,
  useToast,
  Select,
  SimpleGrid,
  Text,
  Link,
  useColorModeValue,
  VStack,
  HStack,
  Input,
  IconButton,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  CheckboxGroup,
  Stack,
  Flex,
  Image,
} from "@chakra-ui/react";
import { AddIcon, HamburgerIcon, EditIcon, DeleteIcon } from "@chakra-ui/icons";
import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import { Link as RouterLink } from "react-router-dom";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { IGateway, IGatewayGroup } from "../../types/deviceInterface";

const calculateBatteryLevel = (voltage: number) =>
  Math.min(Math.round(((voltage - 3230) / 920) * 100), 100);

const getBatteryEmoji = (voltage: number | null) => {
  if (voltage == null) return "❔";
  if (voltage > 4300) return "⚡";
  const pct = calculateBatteryLevel(voltage);
  if (pct > 75) return "🟩";
  if (pct > 50) return "🟨";
  if (pct > 25) return "🟧";
  return "🟥";
};

const GatewaysView = () => {
  const toast = useToast();
  const api = useAxios();
  const apiRef = useRef(api);

  const [gateways, setGateways] = useState<IGateway[]>([]);
  const [gatewayGroups, setGatewayGroups] = useState<IGatewayGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<
    "last_seen" | "number_of_messages_last_hour" | "gateway_name"
  >("last_seen");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [isNewGroupOpen, setIsNewGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newPowerSaving, setNewPowerSaving] = useState(false);
  const [newBanDevices, setNewBanDevices] = useState(false);
  const [newSelectedGateways, setNewSelectedGateways] = useState<number[]>([]);

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [renameGroupId, setRenameGroupId] = useState<number | null>(null);
  const [renameGroupName, setRenameGroupName] = useState("");
  const [renamePowerSavingEnabled, setRenamePowerSavingEnabled] = useState(false);
  const [renameBanDevices, setRenameBanDevices] = useState(false);
  const [renameNumberOfDevices, setRenameNumberOfDevices] = useState(0);
  const { i18n } = useLingui();

  const [assignModal, setAssignModal] = useState<{ open: boolean; gatewayId: number | null }>({
    open: false,
    gatewayId: null,
  });
  const [assignGroupId, setAssignGroupId] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data } = await apiRef.current.get<IGateway[]>("frontend/gateways/");
        setGateways(data);
      } catch (err: any) {
        toast({ title: "Error fetching gateways", description: err.message, status: "error" });
      }
      try {
        const { data } = await apiRef.current.get<IGatewayGroup[]>("frontend/gateway-groups/");
        setGatewayGroups(data);
      } catch (err: any) {
        toast({ title: "Error fetching groups", description: err.message, status: "error" });
      }
    };
    loadData();
  }, [toast]);

  const fetchGroups = async () => {
    try {
      const { data } = await apiRef.current.get<IGatewayGroup[]>("frontend/gateway-groups/");
      setGatewayGroups(data);
    } catch (err: any) {
      toast({ title: "Error fetching groups", description: err.message, status: "error" });
    }
  };

  const displayed =
    selectedGroupId === "all"
      ? [{ id: "all", gateway_group_name: "All Gateways", gateways }]
      : gatewayGroups
          .filter((g) => g.id === selectedGroupId)
          .map((g) => ({
            id: g.id,
            gateway_group_name: g.gateway_group_name,
            gateways: g.gateway_devices,
          }));

  const applySearch = (list: IGateway[]) =>
    list.filter(
      (g) =>
        (g.gateway_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.gateway_address.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const applySort = (list: IGateway[]) =>
    [...list].sort((a, b) => {
      const aVal = (a as any)[sortField];
      const bVal = (b as any)[sortField];
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  const createGroup = async () => {
    try {
      await apiRef.current.post("frontend/gateway-groups/", {
        gateway_group_name: newGroupName,
        power_saving_enabled: newPowerSaving,
        ban_devices: newBanDevices,
        gateway_devices: newSelectedGateways,
      });
      toast({ title: "Group created", status: "success" });
      setIsNewGroupOpen(false);
      fetchGroups();
    } catch (err: any) {
      toast({ title: "Error creating group", description: err.message, status: "error" });
    }
  };

  const renameGroup = async () => {
    if (renameGroupId == null) return;
    try {
      await apiRef.current.patch(`frontend/gateway-groups/${renameGroupId}/`, {
        gateway_group_name: renameGroupName,
        power_saving_enabled: renamePowerSavingEnabled,
        ban_devices: renameBanDevices,
        number_of_devices: renameNumberOfDevices,
      });
      toast({ title: "Group updated", status: "success" });
      setIsRenameOpen(false);
      fetchGroups();
    } catch (err: any) {
      toast({ title: "Error updating group", description: err.message, status: "error" });
    }
  };

  const assignGatewayToGroup = async () => {
    if (assignModal.gatewayId == null || assignGroupId == null) return;
    try {
      await apiRef.current.patch(`frontend/gateway-groups/${assignGroupId}/`, {
        add_gateway: assignModal.gatewayId,
      });
      toast({ title: "Gateway assigned", status: "success" });
      setAssignModal({ open: false, gatewayId: null });
      setAssignGroupId(null);
      fetchGroups();
    } catch (err: any) {
      toast({ title: "Error assigning gateway", description: err.message, status: "error" });
    }
  };

  const dissociateGatewayFromGroup = async (gatewayId: number) => {
    if (selectedGroupId === "all") return;
    try {
      await apiRef.current.patch(`frontend/gateway-groups/${selectedGroupId}/`, {
        remove_gateway: gatewayId,
      });
      toast({ title: "Gateway removed from group", status: "success" });
      fetchGroups();
    } catch (err: any) {
      toast({ title: "Error dissociating gateway", description: err.message, status: "error" });
    }
  };

  const deleteGroup = async (groupId: number) => {
    try {
      await apiRef.current.delete(`frontend/gateway-groups/${groupId}/`);
      toast({ title: "Group deleted", status: "success" });
      if (selectedGroupId === groupId) {
        setSelectedGroupId("all");
      }
      fetchGroups();
    } catch (err: any) {
      toast({ title: "Error deleting group", description: err.message, status: "error" });
    }
  };

  const sidebarBg = useColorModeValue("gray.50", "gray.800");
  const tileColor = useColorModeValue("white", "gray.900");

  return (
    <>
      <Navbar>
        <Flex flexDir={{ base: "column", md: "row" }} align="start" w="100%">
          <Box
            w={{ base: "100%", md: "250px" }}
            p={4}
            bg={sidebarBg}
            boxShadow={"md"}
            borderColor="gray.200"
            height={{ base: "auto", md: "100vh" }}
          >
            <Box>
              <HStack mb={4} justify="space-between">
                <Heading size="md">
                  <Trans>Groups</Trans>
                </Heading>
                <IconButton
                  aria-label="New group"
                  icon={<AddIcon />}
                  size="sm"
                  onClick={() => setIsNewGroupOpen(true)}
                />
              </HStack>
              <VStack align="start" spacing={2} w="100%">
                <Button
                  variant={selectedGroupId === "all" ? "solid" : "ghost"}
                  w="100%"
                  onClick={() => setSelectedGroupId("all")}
                >
                  <Trans>All Gateways</Trans> ({gateways.length})
                </Button>
                {gatewayGroups.map((g) => (
                  <Menu key={g.id}>
                    <MenuButton
                      as={Button}
                      variant={selectedGroupId === g.id ? "solid" : "ghost"}
                      w="100%"
                      rightIcon={<HamburgerIcon />}
                      onClick={() => setSelectedGroupId(g.id)}
                    >
                      {g.gateway_group_name} ({g.gateway_devices.length})
                    </MenuButton>
                    <MenuList>
                      <MenuItem
                        icon={<EditIcon />}
                        onClick={() => {
                          setRenameGroupId(g.id);
                          setRenameGroupName(g.gateway_group_name);
                          setRenamePowerSavingEnabled(g.power_saving_enabled);
                          setRenameBanDevices(g.ban_devices);
                          setRenameNumberOfDevices(g.number_of_devices);
                          setIsRenameOpen(true);
                        }}
                      >
                        <Trans>Configure</Trans>
                      </MenuItem>
                      <MenuItem icon={<DeleteIcon />} onClick={() => deleteGroup(g.id)}>
                        <Trans>Delete</Trans>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                ))}
              </VStack>
            </Box>
          </Box>

          <Box flex="1" p={6}>
            <VStack align="start" mb={6} spacing={4}>
              <Flex justify="space-between" w="100%">
                <Heading>
                  <Trans>Gateways</Trans>
                </Heading>
                <Button as={RouterLink} to="/add-device" size="md">
                  <Trans>Add new Gateway</Trans>
                </Button>
              </Flex>

              <HStack spacing={4} w="100%">
                <Input
                  placeholder={i18n._("Search by name or address")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Select value={sortField} onChange={(e) => setSortField(e.target.value as any)}>
                  <option value="last_seen">
                    <Trans>Last Seen</Trans>
                  </option>
                  <option value="number_of_messages_last_hour">
                    <Trans>Throughput</Trans>
                  </option>
                  <option value="gateway_name">
                    <Trans>Name</Trans>
                  </option>
                </Select>
                <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as any)}>
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </Select>
              </HStack>
            </VStack>

            {displayed.map((section) => (
              <Box key={section.id} mb={8}>
                <Heading size="lg" mb={4}>
                  {section.id === "all" ? <Trans>All Gateways</Trans> : section.gateway_group_name}
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {applySort(applySearch(section.gateways)).map((gw) => (
                    <Box
                      key={gw.id}
                      p={4}
                      shadow="md"
                      borderRadius="md"
                      bg={tileColor}
                      position="relative"
                    >
                      <Menu>
                        <MenuButton position="absolute" top="2" right="2">
                          <HamburgerIcon />
                        </MenuButton>
                        <MenuList>
                          <MenuItem
                            onClick={() => setAssignModal({ open: true, gatewayId: gw.id })}
                          >
                            <Trans>Assign to Group</Trans>
                          </MenuItem>
                          {selectedGroupId !== "all" && (
                            <MenuItem onClick={() => dissociateGatewayFromGroup(gw.id)}>
                              <Trans>Dissociate</Trans>
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>

                      <Link
                        href={`/gateways/gateway-devices/${gw.id}/`}
                        _hover={{ textDecor: "none" }}
                      >
                        <Heading size="md" mb={2}>
                          {gw.gateway_name || "Unnamed Gateway"}
                        </Heading>
                        <Text>
                          <Trans>Address:</Trans> {gw.gateway_address}
                        </Text>
                        <Text>
                          <Trans>Last Seen:</Trans>{" "}
                          {new Date(gw.last_seen).toLocaleString("default", {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Text>
                        <Text>
                          <Trans>Connected devices:</Trans> {gw.number_of_devices}
                        </Text>
                        <Text>
                          <Trans>Throughput:</Trans> {gw.number_of_messages_last_hour} messages/h
                        </Text>
                        <Text>
                          <Trans>Battery:</Trans>
                          {getBatteryEmoji(gw.gateway_battery_voltage)}{" "}
                          {gw.gateway_battery_voltage == null
                            ? "Unknown"
                            : gw.gateway_battery_voltage > 4300
                            ? "Charging"
                            : `${calculateBatteryLevel(gw.gateway_battery_voltage)}%`}
                        </Text>
                      </Link>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </Box>
        </Flex>
      </Navbar>

      <Modal isOpen={isNewGroupOpen} onClose={() => setIsNewGroupOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Create New Group</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Name</Trans>
              </FormLabel>
              <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Power Saving</Trans>
              </FormLabel>
              <Checkbox
                isChecked={newPowerSaving}
                onChange={(e) => setNewPowerSaving(e.target.checked)}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Ban Devices</Trans>
              </FormLabel>
              <Checkbox
                isChecked={newBanDevices}
                onChange={(e) => setNewBanDevices(e.target.checked)}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Gateways</Trans>
              </FormLabel>
              <CheckboxGroup
                value={newSelectedGateways.map(String)}
                onChange={(vals) => setNewSelectedGateways(vals.map(Number))}
              >
                <Stack>
                  {gateways.map((gw) => (
                    <Checkbox key={gw.id} value={gw.id.toString()}>
                      {gw.gateway_name || "Unnamed"} ({gw.gateway_address})
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => setIsNewGroupOpen(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button colorScheme="blue" onClick={createGroup}>
              <Trans>Create</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isRenameOpen} onClose={() => setIsRenameOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Configure Group</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Name</Trans>
              </FormLabel>
              <Input value={renameGroupName} onChange={(e) => setRenameGroupName(e.target.value)} />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Power Saving</Trans>
              </FormLabel>
              <Checkbox
                isChecked={renamePowerSavingEnabled}
                onChange={(e) => setRenamePowerSavingEnabled(e.target.checked)}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Ban Devices</Trans>
              </FormLabel>
              <Checkbox
                isChecked={renameBanDevices}
                onChange={(e) => setRenameBanDevices(e.target.checked)}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Number of Devices</Trans>
              </FormLabel>
              <Input
                type="number"
                value={renameNumberOfDevices}
                onChange={(e) => setRenameNumberOfDevices(Number(e.target.value))}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => setIsRenameOpen(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button colorScheme="blue" onClick={renameGroup}>
              <Trans>Save</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={assignModal.open}
        onClose={() => setAssignModal({ open: false, gatewayId: null })}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Assign Gateway to Group</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>
                <Trans>Select Group</Trans>
              </FormLabel>
              <Select
                placeholder={i18n._("Select group")}
                onChange={(e) => setAssignGroupId(Number(e.target.value))}
              >
                {gatewayGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.gateway_group_name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => setAssignModal({ open: false, gatewayId: null })}>
              <Trans>Cancel</Trans>
            </Button>
            <Button colorScheme="blue" onClick={assignGatewayToGroup}>
              <Trans>Assign</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export { calculateBatteryLevel, getBatteryEmoji };
export default GatewaysView;
